class CartDrawer extends HTMLElement {
  constructor() {
    super();
    // Store bound handlers for proper cleanup
    this.boundHandlers = {
      onEscape: (evt) => evt.code === 'Escape' && this.close(),
      onOverlayClick: this.close.bind(this),
      cartLinkClicks: [],
      cartLinkKeydowns: []
    };
    this.addEventListener('keyup', this.boundHandlers.onEscape);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundHandlers.onOverlayClick);
    this.setHeaderCartIconAccessibility();
  }

  // Cleanup when element is removed from DOM
  disconnectedCallback() {
    this.removeEventListener('keyup', this.boundHandlers.onEscape);
    this.querySelector('#CartDrawer-Overlay')?.removeEventListener('click', this.boundHandlers.onOverlayClick);
    
    // Remove cart link event listeners
    const cartLinks = document.querySelectorAll('.action_cart');
    cartLinks.forEach((item, index) => {
      if (this.boundHandlers.cartLinkClicks[index]) {
        item.removeEventListener('click', this.boundHandlers.cartLinkClicks[index]);
      }
      if (this.boundHandlers.cartLinkKeydowns[index]) {
        item.removeEventListener('keydown', this.boundHandlers.cartLinkKeydowns[index]);
      }
    });

    // Remove summary accessibility event listeners
    if (this.boundHandlers.summaryElement) {
      this.boundHandlers.summaryElement.removeEventListener('click', this.boundHandlers.summaryClick);
    }
    if (this.boundHandlers.summaryParent) {
      this.boundHandlers.summaryParent.removeEventListener('keyup', this.boundHandlers.summaryKeyup);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelectorAll('.action_cart');
    cartLink.forEach((item, index) => {
      // Store handlers for cleanup
      this.boundHandlers.cartLinkClicks[index] = (event) => {
        event.preventDefault();
        this.open(item);
      };
      this.boundHandlers.cartLinkKeydowns[index] = (event) => {
        if (['SPACE', 'ENTER', 'NUMPADENTER'].includes(event.code.toUpperCase())) {
          event.preventDefault();
          this.open(item);
        }
      };
      item.addEventListener('click', this.boundHandlers.cartLinkClicks[index]);
      item.addEventListener('keydown', this.boundHandlers.cartLinkKeydowns[index]);
    });
  }

  open(triggeredBy) {
    // Remember what triggered the drawer so close() can restore focus to it.
    this.setActiveElement(triggeredBy || document.activeElement);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Make the role="dialog" aria-modal="true" declaration true: once the drawer
    // has finished sliding in, trap keyboard focus inside it. `.cart-drawer__inner`
    // is the dialog container and the element that carries the `transform`
    // transition, so wait for its transitionend. The fallback timeout guarantees
    // the trap still runs if transitionend never fires (e.g. a prefers-reduced-motion
    // clamp collapses the transition duration toward zero).
    const container = this.querySelector('.cart-drawer__inner');
    if (container) {
      let trapped = false;
      const trap = () => {
        // Guard: the drawer may have been closed again before the transition
        // finished (overlay click / Escape inside the first ~200ms) — trapping
        // a closed drawer would re-register document listeners and steal focus.
        if (trapped || !this.classList.contains('active')) return;
        trapped = true;
        cleanup();
        // Re-query at trap time: an AJAX re-render may have replaced the
        // drawer's children between open() and the transition end.
        const focusTarget = this.querySelector('.cart-drawer__close') || container;
        trapFocus(container, focusTarget);
      };
      const onTransitionEnd = (event) => {
        if (event.target !== container || event.propertyName !== 'transform') return;
        trap();
      };
      const timer = setTimeout(trap, 400);
      const cleanup = () => {
        container.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(timer);
        this.pendingTrapCleanup = null;
      };
      this.pendingTrapCleanup = cleanup;
      container.addEventListener('transitionend', onTransitionEnd);
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.classList.remove('animate');
    if (this.pendingTrapCleanup) this.pendingTrapCleanup();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    // Store handlers for potential cleanup
    if (!this.boundHandlers.summaryClick) {
      this.boundHandlers.summaryClick = (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      };
      this.boundHandlers.summaryKeyup = onKeyUpEscape;
      this.boundHandlers.summaryElement = cartDrawerNote;
      this.boundHandlers.summaryParent = cartDrawerNote.parentElement;
    }

    cartDrawerNote.addEventListener('click', this.boundHandlers.summaryClick);
    cartDrawerNote.parentElement.addEventListener('keyup', this.boundHandlers.summaryKeyup);
  }

  renderContents(parsedState) {
    this.querySelector('.cart-drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.cart-drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      // {
      //   id: 'cart-icon-bubble',
      // },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Ensure overflow-hidden is removed when page becomes visible (fixes iOS Safari issues)
document.addEventListener('visibilitychange', function() {
  if (!document.hidden) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && !cartDrawer.classList.contains('active')) {
      document.body.classList.remove('overflow-hidden');
    }
  }
});

// Cleanup on page unload/navigation
window.addEventListener('beforeunload', function() {
  document.body.classList.remove('overflow-hidden');
});

// Fix for back button navigation (especially on mobile)
window.addEventListener('pageshow', function(event) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && !cartDrawer.classList.contains('active')) {
    document.body.classList.remove('overflow-hidden');
  }
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.active_cart',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
